import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MapPin, Search as SearchIcon } from "lucide-react";
import { useState } from "react";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import Marquee from "@/components/Marquee";
import { OfferStrip } from "@/components/CampaignBanner";
import { useAuth } from "@/lib/auth";

const NAV_BASE = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/search", label: "Search" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navItems = [...NAV_BASE];
  if (user) {
    navItems.push({ to: "/dashboard", label: "Dashboard" });
  } else {
    navItems.push({ to: "/login", label: "Login" });
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg)]">
      {/* Active campaign offer strip (auto-hides if no live campaign) */}
      <OfferStrip />
      {/* Top marquee */}
      <Marquee />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center">
            <img src="/khasim_logo.png" alt="Khasim Logo" className="h-20 md:h-24 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            {navItems.map((n) => {
              const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={n.label === "Search" ? "flex items-center gap-2 px-4 py-2 rounded-full border-[3px] border-black bg-white text-black hover:bg-[var(--secondary)] transition-all min-w-[200px]" : `flex items-center justify-center px-4 py-2 rounded-full font-bold uppercase text-sm tracking-wide border-[3px] transition-all ${
                    active
                      ? "bg-[var(--primary)] text-white border-black shadow-[3px_3px_0_0_#0A0A0A]"
                      : "border-transparent hover:border-black hover:bg-[var(--secondary)]"
                  }`}
                >
                  {n.label === "Search" ? (
                    <>
                      <SearchIcon size={18} strokeWidth={3} />
                      <span className="text-black/60 font-medium normal-case tracking-normal">Search...</span>
                    </>
                  ) : (
                    n.label
                  )}
                </Link>
              );
            })}
            {user && (
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="px-4 py-2 rounded-full font-bold uppercase text-sm tracking-wide border-[3px] border-transparent hover:border-black hover:bg-[#FF3366] hover:text-white transition-all"
              >
                Logout
              </button>
            )}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden w-11 h-11 rounded-full nb-border bg-white shadow-[3px_3px_0_0_#0A0A0A] flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X strokeWidth={3} /> : <Menu strokeWidth={3} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t-[3px] border-black bg-[var(--secondary)]">
            <div className="px-5 py-4 flex flex-col gap-2">
              {navItems.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={n.label === "Search" ? "flex items-center gap-2 px-4 py-3 rounded-full border-[3px] border-black bg-white text-black hover:bg-[var(--secondary)] transition-all" : "flex items-center px-4 py-3 rounded-full nb-border bg-white font-bold uppercase text-sm shadow-[3px_3px_0_0_#0A0A0A]"}
                >
                  {n.label === "Search" ? (
                    <>
                      <SearchIcon size={18} strokeWidth={3} />
                      <span className="text-black/60 font-medium normal-case tracking-normal">Search...</span>
                    </>
                  ) : (
                    n.label
                  )}
                </Link>
              ))}
              {user && (
                <button
                  onClick={async () => {
                    setOpen(false);
                    await logout();
                    navigate("/");
                  }}
                  className="px-4 py-3 rounded-full nb-border bg-[#FF3366] text-white font-bold uppercase text-sm shadow-[3px_3px_0_0_#0A0A0A] text-left"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-20 border-t-[3px] border-black bg-[var(--primary)] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <img src="/khasim_logo.png" alt="Khasim Logo" className="h-20 md:h-24 w-auto mb-4 object-contain" />
            <p className="text-white/90 max-w-xs">Premium menswear from Machilipatnam — turning everyday fits into candy-bright statements.</p>
          </div>
          <div>
            <div className="font-display font-black uppercase text-lg mb-3">Visit Us</div>
            <div className="flex items-start gap-2">
              <MapPin strokeWidth={2.5} className="mt-1 shrink-0" />
              <p className="text-white/90">2B, Door No 25/1, Near 5 Roads Centre,<br/>Jagannadhapuram, Buttaipet,<br/>Machilipatnam, Andhra Pradesh, India</p>
            </div>
          </div>
          <div>
            <div className="font-display font-black uppercase text-lg mb-3">Get in touch</div>
            <a href="https://wa.me/919885544389" target="_blank" rel="noreferrer" data-testid="footer-wa-link" className="inline-block bg-[var(--secondary)] text-black font-black uppercase tracking-wide px-5 py-3 rounded-full nb-border shadow-[4px_4px_0_0_#0A0A0A]">
              WhatsApp +91 98855 44389
            </a>
          </div>
        </div>
        <div className="border-t-[3px] border-black bg-black py-4 text-center text-white/80 text-xs uppercase tracking-widest font-bold">
          © 2015 KHASIM TRENDZ • Designed & Developed by apexnuera Technologies Machilipatnam
        </div>
      </footer>

      <FloatingWhatsApp />
    </div>
  );
}
